/**
 * useBotFlags — the handful of bot-config booleans the chrome needs, behind a
 * tiny module-level cache so the sidenav and the page share one request.
 *
 * The sidenav's call-logs level only offers the "Users" view when the bot has
 * user memory enabled; CallLogs needs the same flag for the panel itself. Both
 * call this hook, and only the first one triggers `/api/bot/{id}/config`.
 *
 * Deliberately NOT React Query: the Sidenav renders in trees that don't mount a
 * `QueryClientProvider` (tests, embedded surfaces), and a missing provider is a
 * render-time crash. A Map + a subscriber set is enough for one boolean.
 */
import { useEffect, useState } from 'react';
import { useApiHeaders } from '../../hooks/useApiHeaders';
import { useAuthnMe } from '../../AuthnMeProvider';

export interface BotFlags {
  memoryEnabled: boolean;
}

const NO_FLAGS: BotFlags = { memoryEnabled: false };

const cache = new Map<string, BotFlags>();
const inflight = new Map<string, Promise<BotFlags>>();
const listeners = new Set<() => void>();

function load(botId: string, headers: Record<string, string>): Promise<BotFlags> {
  const pending = inflight.get(botId);
  if (pending) return pending;
  const p = fetch(`/api/bot/${botId}/config`, { headers })
    .then((r) => (r.ok ? r.json() : null))
    .then((cfg): BotFlags => ({ memoryEnabled: !!cfg?.memory_enabled }))
    .catch((): BotFlags => NO_FLAGS)
    .then((flags) => {
      cache.set(botId, flags);
      inflight.delete(botId);
      listeners.forEach((cb) => cb());
      return flags;
    });
  inflight.set(botId, p);
  return p;
}

export function useBotFlags(botId: string | undefined): BotFlags {
  const authn = useAuthnMe();
  const apiHeaders = useApiHeaders('');
  const isAuth = !!authn?.isAuthenticated;
  const [, bump] = useState(0);

  useEffect(() => {
    if (!botId || !isAuth || cache.has(botId)) return undefined;
    let alive = true;
    const notify = () => { if (alive) bump((n) => n + 1); };
    listeners.add(notify);
    void load(botId, apiHeaders());
    return () => { alive = false; listeners.delete(notify); };
  }, [botId, isAuth, apiHeaders]);

  return (botId && cache.get(botId)) || NO_FLAGS;
}

/** Test seam — forget everything fetched so far. */
export function resetBotFlagsCache(): void {
  cache.clear();
  inflight.clear();
}
